/**
 * 9.可复用数据表格组件，支持列级样式配置。 Figma：81033:19219
 */

import type { TableColumn, TableRow } from "./type";

import { ChatSignalTag } from "../StructuredCards/common/ChatSignalTag";
import { cn } from "utils";
import { renderDisplayValue } from "../StructuredCards/EarningsCallStructuredCard";
import { useJsonRenderRuntimeContext } from "../jsonRender/JsonRenderRuntimeContext";

export interface AssetDataGridTableProps {
  tableColumns: TableColumn[];
  tableRows: TableRow[];
  firstColumnHeader?: string;
}

export const AssetDataGridTable: React.FC<AssetDataGridTableProps> = ({
  tableColumns,
  tableRows,
  firstColumnHeader = "",
}) => {
  const runtimeContext = useJsonRenderRuntimeContext();
  const stickyBgClass =
    runtimeContext?.scene === "chat"
      ? "bg-background_solid_primary"
      : "bg-background_solid_secondary";

  const columnStyleMap = Object.fromEntries(
    tableColumns.map((column) => [
      column.columnKey,
      column.columnStyle ?? "default",
    ]),
  ) as Record<string, NonNullable<TableColumn["columnStyle"]>>;

  return (
    <div className="w-full overflow-x-auto no-scrollbar">
      <div className="flex min-w-full w-max flex-col">
        <div className="flex w-full border-b border-border_white_transparent10">
          <div
            className={cn(
              "sticky left-0 z-[2] flex w-[82px] shrink-0 items-start p-2 items-center body_s text-content_quaternary",
              stickyBgClass,
            )}
          >
            {firstColumnHeader}
          </div>
          {tableColumns.map((col) => (
            <div
              key={col.columnKey}
              className="flex min-w-[72px] shrink-0 flex-1 items-center justify-center p-2"
            >
              <span className="body_s text-content_quaternary">
                {col.columnTitle}
              </span>
            </div>
          ))}
        </div>
        {tableRows.map((row, idx) => (
          <div
            key={`${row.rowKey}-${idx}`}
            className={cn(
              "flex w-full border-b border-border_white_transparent10 last:border-b-0",
            )}
          >
            <div
              className={cn(
                "sticky left-0 z-[1] flex w-[82px] shrink-0 flex-col justify-center px-2 py-3",
                stickyBgClass,
              )}
            >
              <span className="body_m text-content_tertiary">
                {row.rowName}
              </span>
            </div>
            {row.cells.map((cell, cellIdx) => (
              <div
                key={`${row.rowKey}-${cellIdx}`}
                className="flex min-h-12 min-w-[72px] shrink-0 flex-1 items-center justify-center px-2 py-3"
              >
                {columnStyleMap[cell.columnKey] === "badge" ? (
                  <ChatSignalTag
                    label={cell.cellValue}
                    compact
                    className={`${cell.cellValue.trim().toLowerCase() === "beat" ? "text-content_accent1_primary" : "text-content_accent2_primary"}`}
                  />
                ) : columnStyleMap[cell.columnKey] === "accent" ? (
                  <span
                    className={cn("body_medium_m", {
                      "text-content_accent1_primary":
                        cell.cellValue.startsWith("+"),
                      "text-content_accent2_primary":
                        cell.cellValue.startsWith("-"),
                    })}
                  >
                    {cell.cellValue}
                  </span>
                ) : (
                  renderDisplayValue(
                    cell.cellValue,
                    "body_medium_m text-content_primary",
                  )
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
